import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from "react-intl";

// Controls
import {
    Grid,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Card,
    CardContent,
    ListItemButton,
    Collapse,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';
import { Timezone } from "Components/uiControls";

// Icons
import {
    ExpandLess,
    ExpandMore,
    ChevronLeft,
    ChevronRight
} from '@mui/icons-material';

// Components
import { Checkbox, Select, TextArea, Button } from '@gpstrackit/gtc-react-controls';
import { NotificationManager } from "Components/Notifications";

// Actions
import {
    searchEvents,
    updateCaseEventFlags,
    createDeviceReadingExtraField,
    resetRiskTab,
    updateEventDeviceDataLocally,
    updateEventExtraFieldsLocally,
    searchStatuses
} from './redux/slice';
import { catalogLoad } from "Redux/actions";

// Video Components
import VidFleetVideo from "Modules/.common/components/VidFleetVideo";
import SmartWitnessSnapshot from "Modules/.common/components/SmartWitnessSnapshot";

// Helpers
import { getEventIcon } from "Modules/driverPerformance/.globals/helpers";
import { checkDeviceData } from "Core/data/Helpers";
import { getFormattedDateByTimeZone, GetFormatForMoment } from "Core/data/Helpers";
// Styles
import './index.scss';

const RiskTab = (props) => {
    // Language
    const { messages, locale } = useIntl();

    // State to manage which items are expanded
    const [openItems, setOpenItems] = useState({});

    // State to manage active category and selected event
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedEventIndex, setSelectedEventIndex] = useState(null);
    const [currentComment, setCurrentComment] = useState(null);
    const [isCommentExpanded, setIsCommentExpanded] = useState(false);
    const [openImageModal, setOpenImageModal] = useState(false);

    // Local state for event flags (for save button control)
    const [localEventFlags, setLocalEventFlags] = useState({
        caseEventDisputed: false,
        caseEventOmit: false,
        caseEventStatus: ''
    });

    const dispatch = useDispatch();
    const data = useSelector((state) => state.driverPerformanceRiskTabRedux);

    const caseDetail = useSelector((state) => state.driverPerformanceCaseDetailRedux.caseDetail);
    const updateCaseEventFlagsState = useSelector((state) => state.driverPerformanceRiskTabRedux.updateCaseEventFlags);
    const createDeviceReadingExtraFieldState = useSelector((state) => state.driverPerformanceRiskTabRedux.createDeviceReadingExtraField);

    const statuses = useSelector((state) => state.driverPerformanceRiskTabRedux.statuses);

    const loadingStatuses = useSelector((state) => state.driverPerformanceRiskTabRedux.loadingStatuses);
    const chatUsers = useSelector((state) => state.catalogsRedux.chatUsers);
    const user = useSelector(state => state.securityRedux.user);
    const { dateformat, timeZone } = user || {};

    // Load events when component mounts
    useEffect(() => {
        if (caseDetail && !caseDetail?.updateCoach) {
            dispatch(searchEvents({ caseNumber: caseDetail?.caseNumber }));
        }
    }, [dispatch, caseDetail]);

    useEffect(() => {
        dispatch(searchStatuses({}));
        if (!chatUsers || chatUsers.length === 0) {
            dispatch(catalogLoad(['chatUsers']));
        }
    }, []);

    useEffect(() => {
        if (updateCaseEventFlagsState) {
            if (!updateCaseEventFlagsState.isReadUpdate && updateCaseEventFlagsState.success) {
                NotificationManager.success(
                    messages['driver_performance_riskTab_caseUpdatedCorrectly'],
                    messages['success'],
                    3000
                );
            } if (!updateCaseEventFlagsState.isReadUpdate && !updateCaseEventFlagsState.success) {
                NotificationManager.error(
                    messages['driver_performance_riskTab_errorOccurredWhileUpdatingTheCase'],
                    messages['error'],
                    3000
                );
            }
            dispatch(resetRiskTab({ updateCaseEventFlags: null }));
        }

        if (createDeviceReadingExtraFieldState) {
            if (!createDeviceReadingExtraFieldState.error) {
                NotificationManager.success(
                    messages['driver_performance_riskTab_commentSavedSuccessfully'],
                    messages['success'],
                    3000
                );
                // Update extraField locally in the event
                dispatch(updateEventExtraFieldsLocally({
                    categoryId: activeCategory,
                    eventIndex: selectedEventIndex,
                    newExtraField: createDeviceReadingExtraFieldState
                }));
                // Clear comment field
                setCurrentComment("");
            } else {
                NotificationManager.error(
                    messages['driver_performance_riskTab_errorOccurredWhileCreatingTheComments'],
                    messages['success'],
                    3000,
                );
            }
            // Reset Redux state
            dispatch(resetRiskTab({ createDeviceReadingExtraField: null }));
        }
    }, [updateCaseEventFlagsState, createDeviceReadingExtraFieldState, activeCategory, selectedEventIndex, dispatch])

    // Use categories from Redux directly - the reducer already handles filtering
    const events = useMemo(() => {
        return data.events || [];
    }, [data.events]);

    // Function to handle expansion toggle
    const handleToggle = (itemId) => {
        const isCurrentlyOpen = openItems[itemId];

        if (isCurrentlyOpen) {
            // If open, simply close it
            setOpenItems(prev => ({
                ...prev,
                [itemId]: false
            }));
            setActiveCategory(null);
            setSelectedEventIndex(null);
        } else {
            // If closed, close all other lists first and open this one
            setOpenItems({
                [itemId]: true
            });
            setActiveCategory(itemId);
            // Don't set a selected event, user must click
        }
    };

    // Get active category and its events
    const { activeCategoryData, activeEvents, totalEvents } = useMemo(() => {
        const categoryData = events.find(cat => cat.id === activeCategory);
        const eventsArray = categoryData?.events || [];
        return {
            activeCategoryData: categoryData,
            activeEvents: eventsArray,
            totalEvents: eventsArray.length
        };
    }, [events, activeCategory]);

    // Function to mark an event as read
    const markEventAsRead = (eventIndex) => {
        if (!activeEvents[eventIndex]) return;

        const event = activeEvents[eventIndex];

        // Parse deviceData to get current values
        let currentDeviceData = {};
        if (event.deviceData) {
            try {
                currentDeviceData = typeof event.deviceData === 'string'
                    ? JSON.parse(event.deviceData)
                    : event.deviceData;
            } catch (e) {
                currentDeviceData = {};
            }
        }

        // Normalize caseEventStatus - if it comes as object, extract only the id
        let normalizedStatus = currentDeviceData.caseEventStatus || null;
        if (typeof normalizedStatus === 'object' && normalizedStatus !== null) {
            normalizedStatus = normalizedStatus.id || null;
        }

        // Prepare payload with all current values
        const payload = {
            unitId: event.unitId,
            rowId: event.rowId,
            // Include all current values to not lose them
            caseEventRead: true,
            caseEventDisputed: currentDeviceData.caseEventDisputed || false,
            caseEventOmit: currentDeviceData.caseEventOmit || false,
            caseEventStatus: normalizedStatus
        };

        // Update locally in Redux
        dispatch(updateEventDeviceDataLocally({
            categoryId: activeCategory,
            eventIndex: eventIndex,
            deviceDataUpdates: {
                caseEventRead: true
            }
        }));

        // Update in API
        dispatch(updateCaseEventFlags({ ...payload, isReadUpdate: true }));
    };

    // Function to navigate to next event
    const handleNextEvent = () => {
        if (selectedEventIndex !== null && selectedEventIndex < totalEvents - 1) {
            const newIndex = selectedEventIndex + 1;
            setSelectedEventIndex(newIndex);
            markEventAsRead(newIndex);
        }
    };

    // Function to navigate to previous event
    const handlePrevEvent = () => {
        if (selectedEventIndex !== null && selectedEventIndex > 0) {
            const newIndex = selectedEventIndex - 1;
            setSelectedEventIndex(newIndex);
            markEventAsRead(newIndex);
        }
    };

    // Function to select a specific event
    const handleSelectEvent = (index) => {
        setSelectedEventIndex(index);
        markEventAsRead(index);
    };

    const handleSaveComment = () => {
        if (!currentComment || !processedEventData) return;

        let payload = {
            deviceId: processedEventData.deviceId,
            rowId: processedEventData.rowId,
            fieldName: "noteDriverCase",
            jsonValue: {
                type: "admin",
                description: currentComment
            },
            visibleToDriver: false
        }

        dispatch(createDeviceReadingExtraField(payload));
        // Collapse comment area after saving
        setIsCommentExpanded(false);
    }

    const handleCancelComment = () => {
        setCurrentComment("");
        setIsCommentExpanded(false);
    }

    // Process selected event to get video options
    const processedEventData = useMemo(() => {
        if (selectedEventIndex === null || !activeEvents[selectedEventIndex]) {
            return null;
        }

        const event = activeEvents[selectedEventIndex];
        let deviceData = event && checkDeviceData(event.deviceData);

        if (!deviceData) return null;

        let videoOptions = {
            mode: "picture",
            id: deviceData?.pictureID,
            pictureId: deviceData?.pictureID,
        };

        if (deviceData.clipID) {
            if (deviceData.duration) {
                videoOptions.mode = "video";
            }
            videoOptions.id = deviceData?.clipID;
            videoOptions.pictureId = null;
        }

        if (deviceData.url) {
            videoOptions.url = deviceData?.url;
        }

        videoOptions = {
            ...videoOptions,
            messages: messages,
            type: "playback",
            autoplay: true,
            thumbnailUrl: deviceData.duration === 0
                ? deviceData.url
                : !deviceData?.clipID && !deviceData?.thumbnailUrl
                    ? `/assets/images/clipRequestFailed.png`
                    : deviceData?.thumbnailUrl,
            deviceModel: "4K", // Default, can be determined from unit type if needed
        };

        return {
            rowId: event?.rowId,
            eventName: event?.eventName,
            eventCode: event?.eventCode,
            unitTime: event?.unitTime,
            deviceLabel: event?.deviceLabel,
            driverName: event?.driverName,
            unitType: event?.unitType,
            unitId: event?.unitId,
            deviceId: event?.deviceId,
            deviceData: event,
            videoOptions,
            eventLevel: deviceData?.eventLevel
        };
    }, [selectedEventIndex, activeEvents, messages]);

    // Get current deviceData values from selected event
    const currentEventDeviceData = useMemo(() => {
        if (selectedEventIndex === null || !activeEvents[selectedEventIndex]) {
            return null;
        }

        const event = activeEvents[selectedEventIndex];
        if (!event.deviceData) {
            return null;
        }

        try {
            // Parse deviceData if it's a string
            const deviceData = typeof event.deviceData === 'string'
                ? JSON.parse(event.deviceData)
                : event.deviceData;

            // Return ALL deviceData without overwriting anything
            // Only add default values if they DON'T exist

            // Normalize caseEventStatus - if it comes as object, extract only the id
            let normalizedStatus = deviceData.caseEventStatus ?? '';
            if (typeof normalizedStatus === 'object' && normalizedStatus !== null) {
                normalizedStatus = normalizedStatus.id || '';
            }

            return {
                ...deviceData,
                caseEventRead: deviceData.caseEventRead ?? false,
                caseEventDisputed: deviceData.caseEventDisputed ?? false,
                caseEventOmit: deviceData.caseEventOmit ?? false,
                caseEventStatus: normalizedStatus
            };
        } catch (error) {
            return null;
        }
    }, [selectedEventIndex, activeEvents]);

    // Update local flags when event changes
    useEffect(() => {
        if (currentEventDeviceData) {
            setLocalEventFlags({
                caseEventDisputed: currentEventDeviceData.caseEventDisputed || false,
                caseEventOmit: currentEventDeviceData.caseEventOmit || false,
                caseEventStatus: currentEventDeviceData.caseEventStatus || ''
            });
        }
        // Collapse comment area and close image modal when event changes
        setIsCommentExpanded(false);
        setCurrentComment("");
        setOpenImageModal(false);
    }, [selectedEventIndex, currentEventDeviceData]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!currentEventDeviceData) return false;

        return (
            localEventFlags.caseEventDisputed !== (currentEventDeviceData.caseEventDisputed || false) ||
            localEventFlags.caseEventOmit !== (currentEventDeviceData.caseEventOmit || false) ||
            localEventFlags.caseEventStatus !== (currentEventDeviceData.caseEventStatus || '')
        );
    }, [localEventFlags, currentEventDeviceData]);

    // Process comments from selected event
    const eventComments = useMemo(() => {
        if (selectedEventIndex === null || !activeEvents[selectedEventIndex]) {
            return [];
        }

        const event = activeEvents[selectedEventIndex];
        if (!event.extraFields) {
            return [];
        }

        try {
            // Parse extraFields if it's a string
            const extraFieldsData = typeof event.extraFields === 'string'
                ? JSON.parse(event.extraFields)
                : event.extraFields;

            // Filter only fields that are notes
            if (Array.isArray(extraFieldsData)) {
                const comments = extraFieldsData
                    .filter(field => field.fieldName === 'noteDriverCase')
                    .map((note, index) => ({
                        id: `${event.unitId}-${index}`,
                        description: note.description || '',
                        jsonValue: note.jsonValue || {},
                        type: note.type || '',
                        ...note
                    }));

                return comments;
            }
            return [];
        } catch (error) {
            return [];
        }
    }, [selectedEventIndex, activeEvents, events]);

    // Handler to update local event flags (for disputed and omit)
    const handleUpdateLocalEventFlag = (flagName, flagValue) => {
        setLocalEventFlags(prev => ({
            ...prev,
            [flagName]: flagValue
        }));
    };

    // Handler to cancel event flags changes
    const handleCancelEventFlags = () => {
        if (!currentEventDeviceData) return;

        // Restore local flags to original values from Redux
        setLocalEventFlags({
            caseEventDisputed: currentEventDeviceData.caseEventDisputed || false,
            caseEventOmit: currentEventDeviceData.caseEventOmit || false,
            caseEventStatus: currentEventDeviceData.caseEventStatus || ''
        });
    };

    // Handler to save event flags
    const handleSaveEventFlags = () => {
        if (!processedEventData || !hasUnsavedChanges) return;

        // Normalize caseEventStatus if necessary
        let normalizedStatus = localEventFlags.caseEventStatus || null;
        if (typeof normalizedStatus === 'object' && normalizedStatus !== null) {
            normalizedStatus = normalizedStatus.id || normalizedStatus.entityId || null;
        }

        // Check if we're changing to a status that should remove the event
        const shouldRemoveEvent = normalizedStatus === 'falsepositive' || normalizedStatus === 'noissue';

        if (shouldRemoveEvent) {
            // If removing, dispatch the update action directly without local update
            // to avoid double filtering
            const payload = {
                unitId: processedEventData.unitId,
                rowId: processedEventData.rowId,
                caseEventRead: currentEventDeviceData?.caseEventRead || false,
                caseEventDisputed: localEventFlags.caseEventDisputed,
                caseEventOmit: localEventFlags.caseEventOmit,
                caseEventStatus: normalizedStatus,
                isReadUpdate: false
            };

            // Dispatch the mutation which will trigger the reducer to remove the event
            dispatch(updateCaseEventFlags(payload));
            
            // Clear selection since the event will be removed
            setSelectedEventIndex(null);
        } else {
            // For other status changes, update locally first for immediate UI feedback
            dispatch(updateEventDeviceDataLocally({
                categoryId: activeCategory,
                eventIndex: selectedEventIndex,
                deviceDataUpdates: {
                    caseEventDisputed: localEventFlags.caseEventDisputed,
                    caseEventOmit: localEventFlags.caseEventOmit,
                    caseEventStatus: normalizedStatus
                }
            }));

            // Then sync with backend
            const payload = {
                unitId: processedEventData.unitId,
                rowId: processedEventData.rowId,
                caseEventRead: currentEventDeviceData?.caseEventRead || false,
                caseEventDisputed: localEventFlags.caseEventDisputed,
                caseEventOmit: localEventFlags.caseEventOmit,
                caseEventStatus: normalizedStatus,
                isReadUpdate: false
            };

            dispatch(updateCaseEventFlags(payload));
        }
    };

    return (
        <Grid container spacing={2} className="risk-tab-container">
            {/* Left Panel - Risk Categories */}
            <Grid item xs={12} md={3} className="risk-tab-grid-item">
                <Card className="categories-card">
                    <CardContent className="categories-card-content">
                        {data.loadingEvents ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <CircularProgress />
                            </Box>
                        ) : events.length === 0 ? (
                            <Box className="flex-center min-height-200">
                                <Typography variant="body2" color="textSecondary">
                                    {messages['driver_performance_riskTab_noEventsFound']}
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ paddingLeft: 0 }}>
                                {events.map((category) => (
                                    <React.Fragment key={category.id}>
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                onClick={() => handleToggle(category.id)}
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <div className="category-header">
                                                            <div className="category-name-count">
                                                                <span>{category?.name ? messages[`driver_performance_${category?.name?.replace(/\s+/g, "")}`] || category?.name : 'N/A'}</span>
                                                                <span>({category?.count})</span>
                                                            </div>

                                                            {openItems[category.id] ? (
                                                                <ExpandLess />
                                                            ) : (
                                                                <ExpandMore />
                                                            )}
                                                        </div>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        <Collapse in={openItems[category.id]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {category.events?.map((event, index) => {
                                                    const isSelected = activeCategory === category.id && selectedEventIndex === index;
                                                    const momentFormat = user?.dateformat ? `${GetFormatForMoment(user.dateformat)} h:mm a` : null;
                                                    let date = getFormattedDateByTimeZone(event.unitTime, timeZone, messages, momentFormat, false, locale)
                                                    // Parse deviceData to check if event was read, status and disputed
                                                    let isRead = false;
                                                    let eventStatus = null;
                                                    let isDisputed = false;

                                                    if (event.deviceData) {
                                                        try {
                                                            const deviceData = typeof event.deviceData === 'string'
                                                                ? JSON.parse(event.deviceData)
                                                                : event.deviceData;
                                                            isRead = deviceData.caseEventRead || false;

                                                            // Get status
                                                            eventStatus = deviceData.caseEventStatus;
                                                            if (typeof eventStatus === 'object' && eventStatus !== null) {
                                                                eventStatus = eventStatus.id || null;
                                                            }

                                                            // Get disputed
                                                            isDisputed = deviceData.caseEventDisputed || false;
                                                        } catch (e) {
                                                            isRead = false;
                                                            eventStatus = null;
                                                            isDisputed = false;
                                                        }
                                                    }

                                                    // Get icon for the event
                                                    const eventIcon = getEventIcon(eventStatus, isDisputed, messages);

                                                    return (
                                                        <ListItem
                                                            key={`${event.unitId}-${event.unitTime}-${index}`}
                                                            disablePadding
                                                            sx={{ pl: 2 }}
                                                        >
                                                            <ListItemButton
                                                                onClick={() => handleSelectEvent(index)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    mb: 0.25,
                                                                    backgroundColor: isSelected ? '#005bf8' : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: isSelected ? '#005bf8' : 'action.hover',
                                                                        color: isSelected ? '#cccc' : 'inherit'
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <div className="event-item-content">
                                                                            <Typography
                                                                                variant="caption"
                                                                                color={isSelected ? "#ffffff" : "textSecondary"}
                                                                                sx={{
                                                                                    fontWeight: isRead && !isSelected ? 400 : 600,
                                                                                    color: isSelected ? "#ffffff" : (isRead ? "textSecondary" : "text.primary")
                                                                                }}
                                                                            >
                                                                                {date}
                                                                            </Typography>
                                                                            {eventIcon && (
                                                                                <span className="event-icon-container">
                                                                                    {eventIcon}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            {/* Center Panel - Video/Media Player */}
            <Grid item xs={12} md={6} className="risk-tab-grid-item">
                <Card className="video-card">
                    {activeCategory && totalEvents > 0 && selectedEventIndex !== null ? (
                        <>
                            {/* Contenido del evento seleccionado */}
                            <CardContent className="video-card-content">
                                {processedEventData ? (
                                    <div>
                                        {/* Video/Snapshot Component */}
                                        <div className="video-box" key={`video-${selectedEventIndex}-${processedEventData.unitId}-${processedEventData.unitTime}`}>
                                            {processedEventData.unitType === "lightmetrics" ? (
                                                (() => {
                                                    try {
                                                        // Parse deviceData to get LightMetrics specific data
                                                        let deviceDataParsed = {};
                                                        try {
                                                            deviceDataParsed = typeof processedEventData.deviceData?.deviceData === 'string'
                                                                ? JSON.parse(processedEventData.deviceData.deviceData)
                                                                : processedEventData.deviceData?.deviceData || {};
                                                        } catch (e) {
                                                            deviceDataParsed = {};
                                                        }

                                                        const url = deviceDataParsed?.url;
                                                        const urlExpiration = deviceDataParsed?.urlExpiration;
                                                        const mediaType = deviceDataParsed?.mediaType;
                                                        const isVideoExpired = urlExpiration && new Date(urlExpiration) < new Date();
                                                        const defaultImagePath = locale === "en" ? "/assets/images/no_video_available.png" : "/assets/images/no_video_available_es.png";

                                                        // No URL and no expiration
                                                        if (!url && !urlExpiration) {
                                                            return (
                                                                <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "400px" }}>
                                                                    <img style={{ width: "160px" }} src={defaultImagePath} alt="Video Expired" />
                                                                </div>
                                                            );
                                                        }

                                                        // Video expired
                                                        if (isVideoExpired) {
                                                            return (
                                                                <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "400px" }}>
                                                                    <img style={{ width: "160px" }} src={defaultImagePath} alt="Video Expired" />
                                                                </div>
                                                            );
                                                        }

                                                        // Validate URL exists for rendering
                                                        if (!url) {
                                                            return (
                                                                <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "400px" }}>
                                                                    <img style={{ width: "160px" }} src={defaultImagePath} alt="No URL Available" />
                                                                </div>
                                                            );
                                                        }

                                                        // Picture/Image type
                                                        if (mediaType === "picture") {
                                                            return (
                                                                <>
                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: "400px", backgroundColor: '#000' }}>
                                                                        <img
                                                                            style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', cursor: 'pointer' }}
                                                                            src={url}
                                                                            alt="Event Snapshot"
                                                                            onClick={() => setOpenImageModal(true)}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                const container = e.target.parentElement;
                                                                                if (container) {
                                                                                    container.style.background = '#e5e5e5';
                                                                                    container.innerHTML = `<img style="width: 160px" src="${defaultImagePath}" alt="Error loading image" />`;
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    {openImageModal && url && (
                                                                        <div
                                                                            style={{
                                                                                position: 'fixed',
                                                                                top: 0,
                                                                                left: 0,
                                                                                width: '100vw',
                                                                                height: '100vh',
                                                                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                                                                display: 'flex',
                                                                                justifyContent: 'center',
                                                                                alignItems: 'center',
                                                                                zIndex: 9999,
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => setOpenImageModal(false)}
                                                                        >
                                                                            <img
                                                                                src={url}
                                                                                alt="Event Snapshot Full"
                                                                                style={{
                                                                                    maxWidth: '90vw',
                                                                                    maxHeight: '90vh',
                                                                                    objectFit: 'contain'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    setOpenImageModal(false);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        }

                                                        // Video type
                                                        return (
                                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#000', height: "400px" }}>
                                                                <video
                                                                    controls
                                                                    style={{
                                                                        width: "96%",
                                                                        marginLeft: "2%",
                                                                        height: "100%",
                                                                        position: "relative"
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error('Error loading video:', url);
                                                                    }}
                                                                >
                                                                    <source src={url} type="video/mp4" />
                                                                    {messages["videoMainView_browserNotSupport"] || "Your browser does not support the video tag."}
                                                                </video>
                                                            </div>
                                                        );
                                                    } catch (error) {
                                                        const defaultImagePath = locale === "en" ? "/assets/images/no_video_available.png" : "/assets/images/no_video_available_es.png";
                                                        return (
                                                            <div style={{ cursor: 'not-allowed', background: '#e5e5e5', display: 'flex', justifyContent: 'center', alignItems: 'center', width: "100%", height: "400px" }}>
                                                                <img style={{ width: "160px" }} src={defaultImagePath} alt="Error loading media" />
                                                            </div>
                                                        );
                                                    }
                                                })()
                                            ) : (processedEventData.unitType === "SmartWitness" ||
                                                processedEventData.videoOptions?.deviceModel === "SmartWitness") ? (
                                                <SmartWitnessSnapshot
                                                    key={`smartwitness-${selectedEventIndex}-${processedEventData.unitId}`}
                                                    vehicleTrail={processedEventData.deviceData}
                                                    response={processedEventData}
                                                    height={400}
                                                />
                                            ) : (processedEventData.unitType?.toUpperCase() === "WAYLENS" ||
                                                processedEventData.unitType?.toUpperCase() === "VIDFLEET" ||
                                                processedEventData.videoOptions?.id ||
                                                processedEventData.videoOptions?.url) ? (
                                                <div className="video-container" key={`vidfleet-${selectedEventIndex}-${processedEventData.unitId}`}>
                                                    <VidFleetVideo {...processedEventData.videoOptions} />
                                                </div>
                                            ) : (
                                                <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {messages['driver_performance_riskTab_noVideoDataAvailableForThisEvent']}
                                                    </Typography>
                                                </Box>
                                            )}


                                            <Grid container spacing={1} className="video-controls-grid">
                                                <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Checkbox
                                                        id="driverDisputed"
                                                        checked={localEventFlags.caseEventDisputed}
                                                        onChange={(value) => {
                                                            handleUpdateLocalEventFlag('caseEventDisputed', value);
                                                        }}
                                                        isDisabled={caseDetail?.statusId?.includes('completed')}
                                                    />
                                                    <strong style={{ marginRight: '5px' }}>{messages['driver_performance_riskTab_driverDisputed']}</strong>
                                                </Grid>
                                                <Grid item xs={12} md={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <strong style={{ marginTop: '9px', marginRight: 12 }}>{messages['driver_performance_riskTab_eventStatus']}</strong>
                                                    <Select
                                                        style={{ width: '60%' }}
                                                        id="status"
                                                        options={(statuses || []).map((item) => ({ ...item, label: messages[`driver_performance_${item.entityId}`] || item.label })).filter(status => ["falsepositive", "noissue", "verified"].includes(status.entityId))}
                                                        textField="label"
                                                        valueField="entityId"
                                                        value={(statuses || []).find(status => status?.entityId === localEventFlags.caseEventStatus) || null}
                                                        onChange={(value) => {
                                                            const statusId = typeof value === 'object' && value !== null ? value.entityId : value;
                                                            handleUpdateLocalEventFlag('caseEventStatus', statusId);
                                                        }}
                                                        isLoading={loadingStatuses}
                                                        isDisabled={caseDetail?.statusId?.includes('completed')}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={12} /* style={{ display: 'flex' }} */>
                                                    <div style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                                         <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_drawer_event_type']}:</strong>
                                                         <span>{processedEventData?.eventName ? messages[`driver_performance_${processedEventData?.eventName?.replace(/\s+/g, "")}`] || processedEventData?.eventName : 'N/A'}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                                         <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_drawer_severity']}:</strong>
                                                         <span>{processedEventData?.eventLevel ? messages[`driver_performance_severity_${processedEventData?.eventLevel?.toLowerCase()}`] : 'N/A'}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                                         <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_drawer_category']}:</strong>
                                                         <span>{messages["driver_performance_events_drawer_risk"]}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                                         <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_drawer_source']}:</strong>
                                                         <span>{messages["driver_performance_events_drawer_camera"]}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_drawer_when']}:</strong>
                                                        <div className="event-container-timezone">
                                                            <Timezone
                                                                date={processedEventData?.unitTime}
                                                                dateFormat={dateformat}
                                                                timeFormat="hh:mm a"
                                                                timezone={timeZone}
                                                                className="grey-color"
                                                            />
                                                        </div>
                                                         
                                                    </div>
                                                </Grid>
                                            </Grid>
                                            <Box className={`navigation-box${hasUnsavedChanges ? ' navigation-box--with-actions' : ''}`}>
                                                <div className="navigation-controls">
                                                    <IconButton
                                                        onClick={handlePrevEvent}
                                                        disabled={selectedEventIndex === null || selectedEventIndex === 0}
                                                        size="small"
                                                    >
                                                        <ChevronLeft />
                                                    </IconButton>

                                                    <Typography variant="body2" className="navigation-counter">
                                                        {(selectedEventIndex ?? 0) + 1} of {totalEvents}
                                                    </Typography>

                                                    <IconButton
                                                        onClick={handleNextEvent}
                                                        disabled={selectedEventIndex === null || selectedEventIndex === totalEvents - 1}
                                                        size="small"
                                                    >
                                                        <ChevronRight />
                                                    </IconButton>
                                                </div>
                                                {hasUnsavedChanges && (
                                                    <div className="save-flags-button-container">
                                                        <Button
                                                            id="cancel-flags-button"
                                                            variant="secondary"
                                                            onClick={handleCancelEventFlags}
                                                            isDisabled={!hasUnsavedChanges || !processedEventData}
                                                            className="cancel-flags-button"
                                                        >
                                                            {messages["cancel"]}
                                                        </Button>
                                                        <Button
                                                            id="save-flags-button"
                                                            variant="primary"
                                                            onClick={handleSaveEventFlags}
                                                            isDisabled={!hasUnsavedChanges || !processedEventData}
                                                        >
                                                            {messages["save"]}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Box>
                                        </div>
                                    </div>
                                ) : activeEvents[selectedEventIndex] && (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                                        <Typography variant="body2" color="textSecondary">
                                            {messages['driver_performance_riskTab_noVideoDataAvailableForThisEvent']}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>

                        </>
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <Typography variant="body2" color="textSecondary">
                                {activeCategory && totalEvents > 0
                                    ? messages['driver_performance_riskTab_clickEvent']
                                    : messages['driver_performance_riskTab_selectACategoryToViewEvents']
                                }
                            </Typography>
                        </Box>
                    )}
                </Card>
            </Grid>
            {/* Right Panel - Comments */}
            <Grid item xs={12} md={3} className="risk-tab-grid-item">
                <Card className="comments-card">
                    <CardContent className="comments-card-content">
                        <Typography variant="h6" gutterBottom className="comments-title">
                            {messages['driver_performance_riskTab_comments']}
                        </Typography>
                        {activeCategory && selectedEventIndex !== null ? (
                            eventComments.length > 0 ? (
                                <Grid container direction="column" spacing={0} className="comments-list-container">
                                    {eventComments.map((comment, index) => {
                                        // Parse jsonValue if it's a string
                                        let createUser = chatUsers?.find(x => x.authId == comment.createdBy);

                                        let description = '';
                                        try {
                                            if (typeof comment.jsonValue === 'string') {
                                                const parsed = JSON.parse(comment.jsonValue);
                                                description = parsed.description || '';
                                            } else if (typeof comment.jsonValue === 'object' && comment.jsonValue !== null) {
                                                description = comment.jsonValue.description || '';
                                            }
                                        } catch (e) {
                                            description = comment.jsonValue;
                                        }

                                        return (
                                            <React.Fragment key={comment?.id || index}>
                                                <Grid item xs={12}>
                                                    <div className="comment-box">
                                                        {/* Header: Author (left) and Date (right) */}
                                                        <div className="comment-header">
                                                            <Typography variant="body2" className="comment-author">
                                                                {createUser?.label || comment.createdBy}
                                                            </Typography>
                                                            {comment.createdOn && (
                                                                <div className="event-container-timezone">
                                                                    <Timezone
                                                                        date={comment?.createdOn}
                                                                        dateFormat={dateformat}
                                                                        timeFormat="hh:mm a"
                                                                        timezone={timeZone}
                                                                        className="grey-color"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Comment content */}
                                                        <Typography variant="body2" className="comment-content">
                                                            {description || 'No description'}
                                                        </Typography>
                                                    </div>
                                                </Grid>
                                                {/* Divider between comments (not after the last one) */}
                                                {index < eventComments.length - 1 && (
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <div className="flex-center min-height-200">
                                    <Typography variant="body2" color="textSecondary">
                                        {messages['driver_performance_riskTab_noCommentsAvailableForThisEvent']}
                                    </Typography>
                                </div>
                            )
                        ) : (
                            <div className="flex-center min-height-200 text-center">
                                <Typography variant="body2" color="textSecondary">
                                    {messages['driver_performance_riskTab_selectAnEventToViewComments']}
                                </Typography>
                            </div>
                        )}
                    </CardContent>
                    {
                        processedEventData && (
                            <div className="comment-input-container">
                                {!isCommentExpanded ? (
                                    <div
                                        onClick={() => {
                                            if (!caseDetail?.statusId?.includes('completed')) {
                                                setIsCommentExpanded(true);
                                            }
                                        }}
                                        className="comment-placeholder"
                                    >
                                        {messages['driver_performance_riskTab_addComment']}...
                                    </div>
                                ) : (
                                    <>
                                        <TextArea
                                            id="comments"
                                            value={currentComment}
                                            onChange={(value) => setCurrentComment(value)}
                                            className="comment-textarea"
                                            autoFocus
                                        />
                                        <div className="comment-buttons-container">
                                            <Button
                                                variant="secondary"
                                                className="comment-cancel-button"
                                                onClick={handleCancelComment}
                                            >
                                                {messages["cancel"]}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={() => handleSaveComment()}
                                                isDisabled={!currentComment}
                                            >
                                                {messages["save"]}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    }
                </Card>
            </Grid>
        </Grid>
    );
}

export default RiskTab;
